import { useTranslation } from 'next-i18next'
import React, { useId } from 'react'
import { ILocation } from 'src/types/graphql'
import { DirectoryGuideMode } from 'src/utils/directoryGuide'
import DirectoryGuideCard from './DirectoryGuideCard'

interface DirectoryZeroSupplyProps {
  locations: ILocation[]
  /** The page's own place name ("Tijuana", "Roma Norte"), for the heading. */
  locationName?: string
  /** `zero`: the guide is the page's answer. `weak`: it follows a handful of
   *  SpotMe listings (see `directoryGuideMode`). */
  mode: DirectoryGuideMode
}

/**
 * The CITY GUIDE on a ZERO-SUPPLY city page, SERVER-RENDERED.
 *
 * History: 2026-08-28 (Jules, "be useful for free where we have nothing")
 * moved the directory into the SSR HTML of the ~60 metros with no verified
 * supply. 2026-10-20 (Jules, "give them the presence and usability of SpotMe
 * listings — we have nothing to lose") turned it from a footnote into the
 * page's answer: a counted heading, full-weight cards with the phone one tap
 * away, and the disclaimer reduced to one line under the list. The full
 * disclaimer still opens every ficha.
 *
 * Measured before the change (GSC, MX, Sep 21–Oct 18): these city pages
 * already sit at positions ~6–10 for "mini bodegas <ciudad>"; the guide is
 * the bet that a page that answers the query climbs. Read the late-November
 * GSC check before extending it.
 *
 * Two modes (`directoryGuideMode`): `zero` — the page has no SpotMe listing
 * and the guide is its answer; `weak` (2026-10-20) — a mini-bodega page
 * outside Monterrey with fewer than 15 SpotMe listings, where the guide
 * renders as its own block AFTER them. SpotMe listings of every model always
 * come first; once a city's supply passes the threshold the page goes back to
 * the lazy tier floor on its own.
 *
 * Plain import, no `dynamic()`: the whole point is markup at SSR.
 */
const DirectoryZeroSupply = ({
  locations,
  locationName,
  mode
}: DirectoryZeroSupplyProps) => {
  const { t } = useTranslation('directorio')
  // `useId`, not a literal: SearchList mounts twice (desktop rail + mobile
  // drawer), and a duplicated id would point both regions at one heading.
  const headingId = useId()
  if (locations.length === 0) return null

  const count = locations.length
  const pricedCount = locations.filter(
    (location) => !!location.directoryMeta?.priceFromMxn
  ).length
  // Deliberately no "desde $X" across operators: on a city page that number
  // reads as a SpotMe price claim, and city "Desde" claims come only from
  // `cityPriceFloors.ts`. Each card carries its own attributed price.
  const summary =
    pricedCount > 0
      ? t('GUIDE.SUMMARY_PRICED', { priced: pricedCount, count })
      : t('GUIDE.SUMMARY')
  const surface = mode === 'weak' ? 'search_weak_supply' : 'search_zero_supply'
  const title =
    mode === 'weak'
      ? locationName
        ? t('GUIDE.TITLE_MORE', { count, place: locationName })
        : t('GUIDE.TITLE_MORE_NO_PLACE', { count })
      : locationName
        ? t('GUIDE.TITLE', { count, place: locationName })
        : t('GUIDE.TITLE_NO_PLACE', { count })

  return (
    <section
      className="mt-2"
      aria-labelledby={headingId}
      data-testid="directory-zero-supply"
    >
      <h2
        id={headingId}
        className="font-display text-[21px] leading-tight font-medium text-ink-700 md:text-2xl"
      >
        {title}
      </h2>
      <p className="mt-1.5 max-w-140 text-[14px] leading-relaxed text-ink-200">
        {summary}
      </p>

      <ol className="mt-4 flex flex-col gap-3">
        {locations.map((location, i) => (
          <li key={location.id}>
            <DirectoryGuideCard
              location={location}
              position={i + 1}
              surface={surface}
            />
          </li>
        ))}
      </ol>

      <p className="mt-4 max-w-140 text-[12px] leading-snug text-ink-200">
        {t('GUIDE.NOTE')}
      </p>
    </section>
  )
}

export default React.memo(DirectoryZeroSupply)
